import type { ReactNode } from "react";
import { DraggableAvatarBubble, type BubbleCorner } from "@/components/session/DraggableAvatarBubble";
import { useBubbleSize } from "@/hooks/useBubbleSize";

/**
 * The live-session stage: the avatar alone, or the avatar next to (or over) the
 * document pane, in whichever layout the trainer configured.
 *
 * Purely presentational and provider-neutral — each frame keeps its own SDK
 * wiring and passes the avatar in through `renderAvatar`, which receives the
 * slot so a provider can compose differently for the bubble (small, native
 * ratio) than for a full/side pane.
 */
export function SessionStage({
  documentNode,
  layout = "side-by-side",
  avatarPosition = "right",
  renderAvatar,
}: {
  /** The document pane, or null when there is nothing to show. */
  documentNode: ReactNode;
  layout?: "side-by-side" | "bubble" | string;
  avatarPosition?: "left" | "right";
  renderAvatar: (slot: "full" | "side" | "bubble") => ReactNode;
}) {
  const { containerRef, size, onResizeStart, onResizeMove, onResizeEnd } = useBubbleSize();
  const initialCorner: BubbleCorner = avatarPosition === "left" ? "tl" : "tr";

  if (!documentNode) {
    return <div className="flex-1 min-h-0 relative">{renderAvatar("full")}</div>;
  }

  if (layout === "bubble") {
    return (
      <div ref={containerRef} className="flex-1 min-h-0 relative">
        {documentNode}
        <DraggableAvatarBubble
          initialCorner={initialCorner}
          size={size}
          onResizeStart={onResizeStart}
          onResizeMove={onResizeMove}
          onResizeEnd={onResizeEnd}
        >
          {renderAvatar("bubble")}
        </DraggableAvatarBubble>
      </div>
    );
  }

  return (
    <div
      className={
        "flex-1 min-h-0 flex flex-col lg:flex-row " +
        (avatarPosition === "left" ? "lg:flex-row-reverse" : "")
      }
    >
      <div
        className={
          "flex-[2] min-h-0 lg:flex-none lg:w-2/3 lg:h-full relative " +
          (avatarPosition === "left"
            ? "border-b lg:border-b-0 lg:border-l border-border"
            : "border-b lg:border-b-0 lg:border-r border-border")
        }
      >
        {documentNode}
      </div>
      <div className="flex-1 min-h-0 relative lg:flex-none lg:w-1/3 lg:h-full">
        {renderAvatar("side")}
      </div>
    </div>
  );
}
